/**
 * Sort images by standardization status and shoe type priority.
 *
 * Order: Standardized (shoe sole first) → their originals → remaining images.
 * When isAnalyze is true and standardized images exist, returns only standardized.
 *
 * @param {Array} images         - Raw image array from API
 * @param {boolean} isAnalyze    - If true, return only standardized images when available
 * @returns {Array} Sorted image array (new array, does not mutate input)
 */
export function sortImagesByPriority(images, isAnalyze = false) {
  if (!images || images.length === 0) return [];

  // Helper: get a unique ID for dedup comparison (handles both GalleryCard and History image shapes)
  const getImageId = (img) => img.IDValue ?? img.Image_ID ?? img.ID;

  const sorted = [];

  const shoeSoleTypes = [
    global.config?.Defines?.ImageTypeKey?.SHOE_SOLE_LEFT,
    global.config?.Defines?.ImageTypeKey?.SHOE_SOLE_RIGHT,
    global.config?.Defines?.ImageTypeKey?.SHOE_SOLE_UNDETERMINED,
    global.config?.Defines?.ImageTypeKey?.SHOE_SOLE,
  ];

  // 1. Collect standardized images, shoe soles first
  const arrStandardized = images
    .filter(img => img.IsStandardize === 1)
    .sort((a, b) => {
      if (shoeSoleTypes.includes(a.Type)) return -1;
      if (shoeSoleTypes.includes(b.Type)) return 1;
      return 0;
    });

  // 2. Pair each standardized image with its original
  arrStandardized.forEach((stdImg, index) => {
    if (sorted.find(img => getImageId(img) === getImageId(stdImg))) return;

    sorted.push(stdImg);

    // If the next standardized image shares the same Original_IDValue,
    // defer the original insertion to keep the group together.
    const nextShares =
      index + 1 < arrStandardized.length &&
      arrStandardized[index + 1].Original_IDValue === stdImg.Original_IDValue;

    if (!nextShares) {
      const originalImg = images.find(img => stdImg.Original_IDValue === getImageId(img));
      if (originalImg && !sorted.find(img => getImageId(img) === getImageId(originalImg))) {
        sorted.push(originalImg);
      }
    }
  });

  // 3. Append remaining images that aren't already included
  images.forEach(img => {
    if (!sorted.find(sImg => getImageId(sImg) === getImageId(img))) {
      sorted.push(img);
    }
  });

  // In analyze mode, show only standardized images when available
  if (isAnalyze && arrStandardized.length > 0) {
    return arrStandardized;
  }

  return sorted;
}
